// 框架会提供不同的响应式机制：
// - React 是 useState/useEffect
// - Vue 是 ref/watchEffect
// - Angular 可以用 RxJS + service

// 直接用 RxJS 的问题 / 局限
// 1. 框架原生优化被绕过
// - React 的 useState/useReducer 有批量更新优化
// - Vue 的 ref/reactive 有依赖追踪和模板更新优化
// - 如果完全用 RxJS，你需要手动在每个组件里订阅，并处理卸载/更新
// - 换句话说，会破坏框架对响应式的天然集成，性能可能不如原生 Hook
// 2. 使用体验不一致
// - React 中你必须用 useEffect 或 useSyncExternalStore 来订阅 RxJS
// - Vue 中用 watchEffect 或 from 转成 ref
// - Angular 自然支持 Observable，但模板绑定习惯可能不一样
// - 所以跨框架“用法一致性”还是需要封装一层适配器
// 3. 不够轻量
// - 对于简单状态管理（如计数器、开关）直接用 RxJS 引入库会显得重
// - 对于小型项目，使用 RxJS 可能有点“大材小用”

// 适配器模式的好处
// - 解耦业务逻辑和响应式实现
//   业务逻辑只写一次，不关心 RxJS、Vue Ref 还是 React State
// - 可灵活切换底层实现
//   小项目可以用 React/Vue 原生状态
//   大项目需要复杂流式管理，可以换成 RxJS
// - 统一接口
//   state.getState() / state.setState() 和 actions，组件代码完全不用改

import {
  useState,
  useEffect,
  useLayoutEffect,
  useSyncExternalStore,
  useRef,
  useMemo,
  useCallback,
  useTransition
} from "react";
import { nextTick, ref, watch, watchEffect } from "vue";
import { BehaviorSubject } from "rxjs";
import {
  Callback,
  StateStoreLike,
  Diff,
  State,
  ReduxStore,
  deepEqual,
  deepDiff,
  isArray,
  isPrimitive,
  createSignal,
  execNoThrow
} from "@zyzgroup/core-common";

function createPathMatcher(paths: string[]) {
  const set = new Set(paths);
  return (path: string): boolean => {
    let current = path;
    // eslint-disable-next-line no-constant-condition
    while (true) {
      if (set.has(current)) {
        return true;
      }
      const idx = current.lastIndexOf(".");
      if (idx < 0) {
        return false;
      }
      current = current.slice(0, idx);
    }
  };
}

function getOverlap(diffs: Diff[], watchPaths?: string[]): Diff[] {
  if (!watchPaths || watchPaths.length == 0) {
    return diffs;
  }
  return diffs.filter((diff) =>
    // watchPaths.some((wp) => diff.path.indexOf(wp) >= 0)
    watchPaths.some((wp) => diff.path === wp || diff.path.startsWith(wp + "."))
  );
}

// React：组件重新 render 会反复执行整个 Hook，注意多次渲染过程中不要重复创建function
export function useReactState<T = any>(initial: T): StateStoreLike<T> {
  const [value, setValue] = useState<T>(initial);
  const valueRef = useRef(value); // 解决 stale closure
  // const pendingDiffs = useRef<Diff[]>([]);
  // const signalRef = useRef(createSignal<[T, Diff[]]>());
  const signalRef = useRef(createSignal<[T]>());

  // 使用 useEffect: 确保订阅者在 渲染前 不会读到 旧DOM 或 中间状态
  // useEffect(() => {
  // 保证 DOM commit 前同步
  // useLayoutEffect(() => {
  //   const diffs = pendingDiffs.current;
  //   if (diffs.length) {
  //     signalRef.current.emit(value, diffs);
  //   }
  //   pendingDiffs.current = [];
  // }, [value]);

  // 尝试解决潜在的无限循环: subscribe回调里 最好不要直接修改同一个状态 useReactState.set
  const safeSetValue = useCallback((newValue: T | ((prev: T) => T)) => {
    setValue((prev) => {
      const resolved =
        typeof newValue === "function"
          ? (newValue as (prev: T) => T)(prev)
          : newValue;
      // state is immutable
      if (Object.is(prev, resolved)) {
        // Object.is(NaN, NaN) // true
        // NaN === NaN         // false
        // Object.is(+0, -0)  // false
        // +0 === -0          // true
        // null === null      // true
        // undefined === undefined // true
        return prev;
      }
      // 仅在值真正不同的时候更新
      valueRef.current = resolved;
      // pendingDiffs.current.push(...deepDiff(prev, resolved));
      signalRef.current.emit(resolved);
      return resolved;
    });
  }, []);

  return useMemo(
    () => ({
      getState: () => valueRef.current, // 避免读取的是闭包的 value
      setState: safeSetValue,
      subscribe: (listener: Callback<[T]>) => {
        // 在订阅时立即获取最新值
        execNoThrow(() => listener(valueRef.current));
        const unsubscribe = signalRef.current.subscribe((v) => {
          execNoThrow(() => listener(v));
        });
        return () => unsubscribe();
      }
      // subscribe: (listener: Callback<[T, Diff[]]>, watchPaths?: string[]) => {
      //   const match = watchPaths?.length ? createPathMatcher(watchPaths) : null;
      //   // 在订阅时立即获取最新值
      //   execNoThrow(() => {
      //     listener(valueRef.current, []);
      //   });
      //   return signalRef.current.subscribe((v, d) => {
      //     if (!match) {
      //       execNoThrow(() => {
      //         listener(v, d);
      //       });
      //       return;
      //     }
      //     const overlap = d.filter((x) => match(x.path));
      //     if (overlap.length) {
      //       execNoThrow(() => {
      //         listener(v, overlap);
      //       });
      //     }
      //   });
      // }
    }),
    []
  );
}

// Vue
export function useVueState<T = any>(initial: T): StateStoreLike<T> {
  const state = ref<T>(initial);
  // const diffs = ref<Diff[]>([]);
  // const signal = createSignal<[T, Diff[]]>();
  const signal = createSignal<[T]>();

  // watch(
  //   state,
  //   () => {
  //     signal.emit(state.value, diffs.value);
  //   },
  //   { flush: "post" } // 保证 DOM 已更新，已是 nextTick
  // );

  // 尝试解决潜在的无限循环: subscribe回调里 最好不要直接修改同一个状态 useVueState.set
  const safeSet = (v: T | ((prev: T) => T)) => {
    const resolved =
      typeof v === "function" ? (v as (prev: T) => T)(state.value) : v;
    // state is immutable
    if (Object.is(state.value, resolved)) {
      // diffs.value = [];
      return;
    }
    // 仅在值真正不同的时候更新
    // diffs.value = deepDiff(state.value, resolved);
    state.value = resolved;
    signal.emit(resolved);
  };

  return {
    getState: () => state.value,
    setState: safeSet,
    subscribe: (listener: Callback<[T]>) => {
      // 在订阅时立即获取最新值
      execNoThrow(() => listener(state.value));
      return signal.subscribe((v) => {
        execNoThrow(() => listener(v));
      });
    }
    // subscribe: (listener: Callback<[T, Diff[]]>, watchPaths?: string[]) => {
    //   const match = watchPaths?.length ? createPathMatcher(watchPaths) : null;
    //   // 在订阅时立即获取最新值
    //   execNoThrow(() => {
    //     listener(state.value, []);
    //   });
    //   return signal.subscribe((v, d) => {
    //     if (!match) {
    //       execNoThrow(() => {
    //         listener(v, d);
    //       });
    //       return;
    //     }
    //     const overlap = d.filter((x) => match(x.path));
    //     if (overlap.length) {
    //       execNoThrow(() => {
    //         listener(v, overlap);
    //       });
    //     }
    //   });
    // }
  };
}

// Reducer must be pure
// 一个巨大的 reducer
// export function defaultStateStoreReducer<T = any>(prevState: T): T {
//   if (isPrimitive(prevState)) {
//     return prevState;
//   }
//   const nextState: T = isArray(prevState)
//     ? ([...(prevState as any[])] as T)
//     : { ...prevState };
//   return nextState;
// }

// ** Demo in Once **
// export function useCounter(state: StateStoreLike<number>) {
//   const increment = () => state.set(state.get() + 1);
//   const decrement = () => state.set(state.get() - 1);
//   return { state, increment, decrement };
// }

// ** React use Demo **
// function Counter() {
//   const { state, increment, decrement } = useCounter(useReactState(0));
//   return (
//     <div>
//       <p>{state.get()}</p>
//       <button onClick={increment}>+</button>
//       <button onClick={decrement}>-</button>
//     </div>
//   );
// }

// ** Vue use Demo **
// export default {
//   setup() {
//     const { state, increment, decrement } = useCounter(useVueState(0));
//     return { state, increment, decrement };
//   },
//   template: `
//     <div>
//       <p>{{ state.get() }}</p>
//       <button @click="increment">+</button>
//       <button @click="decrement">-</button>
//     </div>
//   `
// };

// ** Angular use Demo **
// @Component({
//   selector: "app-counter",
//   template: `
//     <div>
//       <p>{{ count }}</p>
//       <button (click)="increment()">+</button>
//       <button (click)="decrement()">-</button>
//     </div>
//   `
// })
// export class CounterComponent {
//   counter = useCounter(rxState(0));
//   count = this.counter.state.get();
//   increment = this.counter.increment;
//   decrement = this.counter.decrement;
//   constructor() {
//     this.counter.state.subscribe((v) => (this.count = v));
//   }
// }
